import { useEffect, useMemo, useState } from 'react'
import { useRecoilState } from 'recoil'
import { useExchange } from '../hooks/useExchange'
import { useBroadcastChannel } from '../hooks/useBroadcastChannel'
import { createExchange, startUpload, uploadToS3, dataUrlToBlob } from '../api/services'
import { ApiError } from '../api/client'
import type { ExchangeCreateRequest, Passport } from '../api/types'
import type { ReceiptTransactionData } from '@abcpay/shared'
import { printReceipt } from '../utils/printer'
import { lastTransactionAtom } from '../state/atoms'
import { currencies, CURRENCY_MIN_UNITS, roundKRW } from '../constants/currencies'
import CurrencyCard from './CurrencyCard'
import FlagIcon from './FlagIcon'
import Keypad from './Keypad'
import ConfirmModal from './ConfirmModal'
import LoadingModal from './LoadingModal'
import PassportScanModal from './PassportScanModal'
import RateModificationModal from './RateModificationModal'
import ForeignAmountModificationModal from './ForeignAmountModificationModal'

// 더블 탭 감지 딜레이 (ms)
const DOUBLE_TAP_DELAY = 300

interface RateOverride {
  rate: number
  reason: string
}

/**
 * 통화간 교환 탭 (#7)
 * A 통화를 매입환율로 원화 환산 → 그 원화로 B 통화를 매각환율로 계산해 지급.
 * 남는 원화는 거스름돈. 서버에는 매입 1건 + 매각 1건으로 기록.
 */
function ExchangeTab() {
  const { selectedBranch, getRate, isLoadingRates } = useExchange()
  const [lastTransaction, setLastTransaction] = useRecoilState(lastTransactionAtom)
  const { sendTransactionUpdate, sendDisplayModeChange } = useBroadcastChannel({
    windowId: 'staff-pos',
  })

  // 통화 선택: 첫 번째 탭 → A(손님이 낸 통화), 두 번째 탭 → B(손님이 받을 통화)
  const [currencyA, setCurrencyA] = useState<string | null>(null)
  const [currencyB, setCurrencyB] = useState<string | null>(null)
  const [amountA, setAmountA] = useState<string>('')
  const [bOverride, setBOverride] = useState<number | null>(null)

  // 환율 수정 (A: 매입환율, B: 매각환율 각각)
  const [rateOverrideA, setRateOverrideA] = useState<RateOverride | null>(null)
  const [rateOverrideB, setRateOverrideB] = useState<RateOverride | null>(null)
  const [rateModalTarget, setRateModalTarget] = useState<'A' | 'B' | null>(null)
  const [lastTapA, setLastTapA] = useState(0)
  const [lastTapB, setLastTapB] = useState(0)
  const [showBAmountModal, setShowBAmountModal] = useState(false)
  const [lastTapBAmount, setLastTapBAmount] = useState(0)

  // 거래 진행 상태
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('거래 처리 중...')
  const [transactionError, setTransactionError] = useState<string | null>(null)
  const [showReprintButton, setShowReprintButton] = useState(false)
  const [showPassportScanModal, setShowPassportScanModal] = useState(false)
  const [scannedPassportImage, setScannedPassportImage] = useState<string | null>(null)
  const [passportSkipped, setPassportSkipped] = useState(false)

  const infoA = useMemo(() => currencies.find(c => c.code === currencyA), [currencyA])
  const infoB = useMemo(() => currencies.find(c => c.code === currencyB), [currencyB])

  // 환율: A는 매입(buy), B는 매각(sell)
  const baseRateA = currencyA ? getRate(currencyA, 'buy') : 0
  const baseRateB = currencyB ? getRate(currencyB, 'sell') : 0
  const effRateA = rateOverrideA?.rate ?? baseRateA
  const effRateB = rateOverrideB?.rate ?? baseRateB

  // 계산: A 외화 → 원화 → B 외화 + 거스름돈
  const calc = useMemo(() => {
    const amtA = parseFloat(amountA) || 0
    const krwFromA = amtA > 0 && effRateA > 0 ? roundKRW(amtA * effRateA) : 0

    if (!currencyB || effRateB <= 0 || krwFromA <= 0) {
      return { amtA, krwFromA, calcB: 0, amtB: 0, krwForB: 0, changeKRW: 0 }
    }

    const minUnit = CURRENCY_MIN_UNITS[currencyB] || 1
    const calcB = Math.floor(krwFromA / effRateB / minUnit) * minUnit
    const amtB = bOverride ?? calcB
    const krwForB = roundKRW(amtB * effRateB)
    const changeKRW = krwFromA - krwForB

    return { amtA, krwFromA, calcB, amtB, krwForB, changeKRW }
  }, [amountA, effRateA, effRateB, currencyB, bOverride])

  const isReadyToRegister =
    calc.amtA > 0 && calc.krwFromA > 0 && !!currencyA && !!currencyB &&
    calc.amtB > 0 && calc.changeKRW >= 0

  // 통화 선택: A → B 순서로 채우고, 둘 다 있으면 B를 교체
  const handleSelectCurrency = (code: string) => {
    if (showReprintButton) return
    if (!currencyA) {
      setCurrencyA(code)
      return
    }
    if (code === currencyA || code === currencyB) return
    setCurrencyB(code)
    setBOverride(null)
    setRateOverrideB(null)
  }

  const handleReset = () => {
    setCurrencyA(null)
    setCurrencyB(null)
    setAmountA('')
    setBOverride(null)
    setRateOverrideA(null)
    setRateOverrideB(null)
    setTransactionError(null)
    setShowReprintButton(false)
    setScannedPassportImage(null)
    setPassportSkipped(false)
    setShowConfirmModal(false)
  }

  const handleNewTransaction = () => {
    setLastTransaction({ transactionData: null, exchangeResponse: null })
    handleReset()
  }

  // 더블 탭 → 환율 수정 모달
  const handleRateTapA = () => {
    if (!currencyA) return
    const now = Date.now()
    if (now - lastTapA < DOUBLE_TAP_DELAY) {
      setRateModalTarget('A')
      setLastTapA(0)
    } else setLastTapA(now)
  }
  const handleRateTapB = () => {
    if (!currencyB) return
    const now = Date.now()
    if (now - lastTapB < DOUBLE_TAP_DELAY) {
      setRateModalTarget('B')
      setLastTapB(0)
    } else setLastTapB(now)
  }

  // 더블 탭 → B 지급 외화 수정 모달
  const handleBAmountTap = () => {
    if (!currencyB || calc.calcB <= 0) return
    const now = Date.now()
    if (now - lastTapBAmount < DOUBLE_TAP_DELAY) {
      setShowBAmountModal(true)
      setLastTapBAmount(0)
    } else setLastTapBAmount(now)
  }

  // A 금액/환율 변동 시 B 수동 수정값 초기화 (기준이 바뀌므로 재계산)
  useEffect(() => {
    setBOverride(null)
  }, [amountA, effRateA, currencyB])

  // 고객 디스플레이 전송
  useEffect(() => {
    if (!currencyA || calc.amtA <= 0 || !effRateA) {
      sendDisplayModeChange({ mode: 'idle' })
      sendTransactionUpdate({
        amount: 0, currency: currencyA || 'USD', rate: 0, krwAmount: 0,
        tradeType: 'buy', status: 'idle',
      })
      return
    }

    const status = showReprintButton ? 'complete' : 'processing'
    sendDisplayModeChange({ mode: showReprintButton ? 'complete' : 'transaction' })
    sendTransactionUpdate({
      amount: calc.amtA,
      currency: currencyA,
      rate: effRateA,
      krwAmount: calc.krwFromA,
      tradeType: 'buy',
      status,
      ...(currencyB && calc.amtB > 0 && {
        exchange: {
          give: { currency: currencyA, amount: calc.amtA, rate: effRateA, krwAmount: calc.krwFromA },
          receive: { currency: currencyB, amount: calc.amtB, rate: effRateB, krwAmount: calc.krwForB },
          changeKRW: calc.changeKRW,
        },
      }),
    })
  }, [currencyA, currencyB, calc, effRateA, effRateB, showReprintButton, sendDisplayModeChange, sendTransactionUpdate])

  const handleComplete = async () => {
    if (!selectedBranch || !currencyA || !currencyB) return

    setIsCreating(true)
    setTransactionError(null)

    try {
      let passportData: Passport = 'Unknown'
      if (scannedPassportImage) {
        setLoadingMessage('여권사진 업로드중...')
        const uploadResponse = await startUpload({
          branchNid: selectedBranch.nid,
          fileType: 'passport',
          contentType: 'image/jpeg',
        })
        const blob = dataUrlToBlob(scannedPassportImage)
        await uploadToS3(uploadResponse.url, blob, 'image/jpeg')
        passportData = { number: '', imageKey: uploadResponse.key }
      }

      // 1. 매입 거래 (A → KRW)
      setLoadingMessage(`${currencyA} 매입 등록중... (1/2)`)
      const buyRequest: ExchangeCreateRequest = {
        branchNid: selectedBranch.nid,
        fromCurrency: currencyA,
        fromAmount: calc.amtA.toString(),
        toCurrency: 'KRW',
        customer: { passport: passportData },
        expectedToAmount: calc.krwFromA.toString(),
        memo: rateOverrideA
          ? `교환 거래 (매입) / 환율변경: ${rateOverrideA.rate}, 사유: ${rateOverrideA.reason}`
          : '교환 거래 (매입)',
      }
      const buyResponse = await createExchange(buyRequest)

      // 2. 매각 거래 (KRW → B)
      setLoadingMessage(`${currencyB} 매각 등록중... (2/2)`)
      let sellNid: string
      try {
        const sellRequest: ExchangeCreateRequest = {
          branchNid: selectedBranch.nid,
          fromCurrency: 'KRW',
          fromAmount: calc.krwForB.toString(),
          toCurrency: currencyB,
          customer: { passport: passportData },
          expectedToAmount: calc.amtB.toString(),
          memo: rateOverrideB
            ? `교환 거래 (매각) / 환율변경: ${rateOverrideB.rate}, 사유: ${rateOverrideB.reason}`
            : '교환 거래 (매각)',
        }
        const sellResponse = await createExchange(sellRequest)
        sellNid = sellResponse.exchange.nid
      } catch (sellError) {
        // 매입은 이미 등록된 상태 — 중복 등록 방지를 위해 상태를 유지하고 명확히 안내
        const msg = sellError instanceof ApiError ? sellError.message : '오류'
        setTransactionError(
          `${currencyA} 매입(${buyResponse.exchange.nid})은 등록되었으나 ${currencyB} 매각 등록에 실패했습니다: ${msg} — 기록 탭에서 매입 건을 확인 후 취소하거나 매각을 수동 처리해주세요.`
        )
        return
      }

      // 3. 영수증 데이터 (교환: 매입+매각+거스름돈 한 장)
      const transactionData: ReceiptTransactionData = {
        transactionNumber: buyResponse.exchange.nid,
        selectedCurrency: currencyA,
        rate: effRateA.toString(),
        foreignAmount: calc.amtA.toString(),
        krwAmount: calc.krwFromA.toString(),
        tradeType: 'buy',
        timestamp: new Date(),
        exchangePair: {
          buy: {
            currency: currencyA,
            rate: effRateA.toString(),
            foreignAmount: calc.amtA.toString(),
            krwAmount: calc.krwFromA.toString(),
            transactionNumber: buyResponse.exchange.nid,
          },
          sell: {
            currency: currencyB,
            rate: effRateB.toString(),
            foreignAmount: calc.amtB.toString(),
            krwAmount: calc.krwForB.toString(),
            transactionNumber: sellNid,
          },
          changeKRW: calc.changeKRW.toString(),
        },
      }

      setLastTransaction({ transactionData, exchangeResponse: buyResponse.exchange })

      const printResult = await printReceipt(transactionData)
      if (printResult.success) {
        setShowReprintButton(true)
      } else {
        setTransactionError(`거래는 등록되었으나 영수증 발행에 실패했습니다: ${printResult.message || ''}`)
        setShowReprintButton(true)
      }
    } catch (error) {
      console.error('교환 거래 생성 실패:', error)
      setTransactionError(
        error instanceof ApiError ? `거래 생성 실패: ${error.message}` : '거래 생성 중 오류가 발생했습니다.'
      )
    } finally {
      setIsCreating(false)
    }
  }

  const handleReprint = async () => {
    if (!lastTransaction?.transactionData) return
    setLoadingMessage('재출력중...')
    setIsCreating(true)
    const printResult = await printReceipt(lastTransaction.transactionData)
    setIsCreating(false)
    if (!printResult.success) {
      setTransactionError(`재출력 실패: ${printResult.message || '프린터 연결을 확인해주세요.'}`)
    }
  }

  // 환율 표시 박스 (A/B 공통)
  const renderRateBox = (
    label: string,
    effRate: number,
    baseRate: number,
    override: RateOverride | null,
    onTap: () => void,
  ) => (
    <div
      onClick={onTap}
      className="cursor-pointer text-right px-3 py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
    >
      <div className="text-[13px] text-fx-text-muted">
        {label}
        {override && (
          <span className="ml-1.5 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">수정됨</span>
        )}
      </div>
      {isLoadingRates ? (
        <div className="text-lg text-gray-400">로딩 중...</div>
      ) : effRate > 0 ? (
        <div className="flex items-baseline justify-end gap-1.5">
          <span className={`text-xl font-bold ${override ? 'text-orange-600' : 'text-fx-text-dark'}`}>
            {effRate.toLocaleString()}
          </span>
          {override && (
            <span className="text-xs text-gray-400 line-through">({baseRate.toLocaleString()})</span>
          )}
        </div>
      ) : (
        <div className="text-lg text-red-500">환율 없음</div>
      )}
      {!override && effRate > 0 && (
        <div className="text-sm text-gray-400">두 번 탭하여 환율 변경</div>
      )}
    </div>
  )

  return (
    <>
      {transactionError && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg">
          {transactionError}
        </div>
      )}

      <div className="flex gap-6 flex-1 min-h-0">
        {/* 왼쪽: 통화 그리드 (매입/매각 화면과 동일한 그리드, A → B 순서로 선택) */}
        <div className={`flex-1 min-w-0 bg-fx-currency-bg p-6 rounded-lg overflow-y-auto transition-opacity duration-300 ${showReprintButton ? 'opacity-50 pointer-events-none' : ''}`}>
          <div className="grid grid-cols-4 gap-3">
            {currencies.map((currency) => {
              const isA = currency.code === currencyA
              const isB = currency.code === currencyB
              return (
                <CurrencyCard
                  key={currency.code}
                  code={currency.code}
                  flagCode={currency.countryCode}
                  isSelected={isA || isB}
                  cornerLabel={isA ? 'A 매입' : isB ? 'B 매각' : undefined}
                  cornerClass={isA ? 'bg-fx-buy' : 'bg-fx-sell'}
                  onClick={() => handleSelectCurrency(currency.code)}
                />
              )
            })}
          </div>
        </div>

        {/* 중앙: A → 원화 → B 계산 패널 */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-4 overflow-y-auto">
          {/* A: 손님이 내는 통화 (매입) */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 flex flex-col gap-4 flex-none">
            {currencyA && infoA ? (
              <>
                <div className="flex items-center gap-3">
                  <FlagIcon countryCode={infoA.countryCode} size="xl" className="text-5xl" title={infoA.name} />
                  <div>
                    <div className="text-3xl font-bold text-fx-text-dark">{currencyA}</div>
                    <div className="text-sm text-fx-buy font-bold">A · 손님이 낸 통화 (매입)</div>
                  </div>
                  <div className="flex-1" />
                  {renderRateBox('매입 환율', effRateA, baseRateA, rateOverrideA, handleRateTapA)}
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
                  <div>
                    <label className="block text-sm font-medium text-fx-text-muted mb-1.5">외화 금액</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={calc.amtA > 0 ? calc.amtA.toLocaleString('ko-KR') : ''}
                      onChange={(e) => setAmountA(e.target.value.replace(/[^0-9]/g, ''))}
                      className="w-full h-14 px-3 border-2 border-fx-buy bg-blue-50/40 rounded-lg focus:ring-2 focus:ring-fx-buy focus:border-transparent text-right text-2xl font-bold disabled:opacity-50"
                      placeholder="0"
                      disabled={isLoadingRates || !effRateA || showReprintButton}
                    />
                  </div>
                  <span className="text-xl text-gray-400 pb-3">→</span>
                  <div>
                    <label className="block text-sm font-medium text-fx-text-muted mb-1.5 text-right">원화 금액</label>
                    <div className="h-14 flex items-center justify-end text-[2rem] leading-none font-bold text-fx-text-dark">
                      {calc.krwFromA.toLocaleString()}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="py-10 text-center text-fx-text-muted">
                손님이 내는 통화(A)를 왼쪽에서 선택하세요
              </div>
            )}
          </div>

          {/* B: 손님이 받을 통화 (매각) */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 flex flex-col gap-4 flex-none">
            {currencyB && infoB ? (
              <>
                <div className="flex items-center gap-3">
                  <FlagIcon countryCode={infoB.countryCode} size="xl" className="text-5xl" title={infoB.name} />
                  <div>
                    <div className="text-3xl font-bold text-fx-text-dark">{currencyB}</div>
                    <div className="text-sm text-fx-sell font-bold">B · 손님이 받을 통화 (매각)</div>
                  </div>
                  <div className="flex-1" />
                  {renderRateBox('매각 환율', effRateB, baseRateB, rateOverrideB, handleRateTapB)}
                </div>
                <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-end">
                  <div>
                    <label className="block text-sm font-medium text-fx-text-muted mb-1.5">원화 금액</label>
                    <div className="h-14 flex items-center justify-start text-[2rem] leading-none font-bold text-fx-text-dark">
                      {calc.krwForB.toLocaleString()}
                    </div>
                  </div>
                  <span className="text-xl text-gray-400 pb-3">→</span>
                  <div onClick={handleBAmountTap} className="cursor-pointer hover:bg-gray-50 rounded-lg transition-colors">
                    <div className="flex items-center justify-end gap-1.5 mb-1.5">
                      <label className="text-sm font-medium text-fx-text-muted">지급 외화</label>
                      {bOverride !== null && (
                        <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">수정됨</span>
                      )}
                    </div>
                    <div className="h-14 flex items-center justify-end gap-2">
                      <span className={`text-[2rem] leading-none font-bold ${bOverride !== null ? 'text-orange-600' : 'text-fx-sell-dark'}`}>
                        {calc.amtB.toLocaleString()} {currencyB}
                      </span>
                      {bOverride !== null && (
                        <span className="text-xs text-gray-400 line-through">({calc.calcB.toLocaleString()})</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-400 text-right">두 번 탭하여 외화 수정</div>
                  </div>
                </div>
                <div className={`flex items-center justify-between rounded-lg px-4 py-3 ${calc.changeKRW < 0 ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <span className={`text-base font-bold ${calc.changeKRW < 0 ? 'text-red-700' : 'text-yellow-700'}`}>
                    거스름돈 (원화)
                  </span>
                  <span className={`text-3xl font-bold ${calc.changeKRW < 0 ? 'text-red-700' : 'text-yellow-700'}`}>
                    {calc.changeKRW.toLocaleString()} 원
                  </span>
                </div>
                {calc.changeKRW < 0 && (
                  <p className="text-sm text-red-600">
                    지급 외화가 손님이 낸 금액보다 큽니다. 지급 외화를 줄여주세요.
                  </p>
                )}
              </>
            ) : (
              <div className="py-10 text-center text-fx-text-muted">
                {currencyA ? '손님이 받을 통화(B)를 왼쪽에서 선택하세요' : ''}
                {currencyA && calc.krwFromA > 0 && (
                  <div className="mt-2 text-fx-text-dark text-lg font-bold">
                    현재 원화 환산: {calc.krwFromA.toLocaleString()} 원
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 오른쪽: 키패드 + 등록 */}
        <div className="w-64 lg:w-72 fhd:w-80 flex flex-col gap-3">
          <Keypad
            value={amountA}
            onChange={setAmountA}
            disabled={!currencyA || isLoadingRates || !effRateA || showReprintButton}
          />

          <button
            type="button"
            onClick={handleReset}
            disabled={showReprintButton}
            className="w-full h-12 text-base font-bold bg-fx-reset text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50"
          >
            전체지우기
          </button>

          {!showReprintButton ? (
            <button
              type="button"
              onClick={() => setShowPassportScanModal(true)}
              className={`w-full h-14 lg:h-16 fhd:h-20 text-lg lg:text-xl font-bold bg-white border-2 rounded-lg hover:bg-gray-50 active:bg-gray-100 transition-all ${
                scannedPassportImage
                  ? 'text-green-600 border-green-500'
                  : passportSkipped
                    ? 'text-gray-500 border-gray-400'
                    : isReadyToRegister
                      ? 'text-fx-text-dark border-gray-300 animate-border-pulse'
                      : 'text-fx-text-dark border-gray-300'
              }`}
            >
              {scannedPassportImage ? '여권/신분증 ✓' : passportSkipped ? '여권/신분증 (SKIP)' : '여권/신분증'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleReprint}
              disabled={isCreating}
              className="w-full h-14 lg:h-16 fhd:h-20 text-lg lg:text-xl font-bold bg-blue-500 text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all disabled:opacity-50"
            >
              {isCreating ? '재출력 중...' : '영수증 재출력'}
            </button>
          )}

          {!showReprintButton ? (
            <button
              type="button"
              onClick={() => setShowConfirmModal(true)}
              className="w-full h-14 lg:h-16 fhd:h-20 text-lg lg:text-xl font-bold bg-violet-500 text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!isReadyToRegister || isLoadingRates || (!scannedPassportImage && !passportSkipped)}
            >
              교환등록
            </button>
          ) : (
            <button
              type="button"
              onClick={handleNewTransaction}
              className="w-full h-14 lg:h-16 fhd:h-20 text-lg lg:text-xl font-bold bg-fx-confirm text-white rounded-lg hover:opacity-90 active:opacity-80 transition-all"
            >
              새 거래
            </button>
          )}
        </div>
      </div>

      {/* 교환 확인 모달 */}
      <ConfirmModal
        isOpen={showConfirmModal}
        title="교환 등록"
        confirmText="확인"
        cancelText="취소"
        onConfirm={() => {
          setShowConfirmModal(false)
          handleComplete()
        }}
        onCancel={() => setShowConfirmModal(false)}
      >
        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-fx-text-muted">받는 외화 (매입 {currencyA})</span>
            <span className="text-xl font-bold text-fx-text-dark">
              {calc.amtA.toLocaleString()} {currencyA}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-fx-text-muted">매입 환율 {rateOverrideA && '(수정됨)'}</span>
            <span className={rateOverrideA ? 'text-orange-600' : 'text-fx-text-dark'}>
              {effRateA.toLocaleString()} → {calc.krwFromA.toLocaleString()} 원
            </span>
          </div>
          <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
            <span className="text-fx-text-muted">주는 외화 (매각 {currencyB})</span>
            <span className="text-xl font-bold text-fx-sell-dark">
              {calc.amtB.toLocaleString()} {currencyB}
            </span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className="text-fx-text-muted">매각 환율 {rateOverrideB && '(수정됨)'}</span>
            <span className={rateOverrideB ? 'text-orange-600' : 'text-fx-text-dark'}>
              {effRateB.toLocaleString()} → {calc.krwForB.toLocaleString()} 원
            </span>
          </div>
          <div className="border-t-2 border-gray-300 pt-3 flex justify-between items-center">
            <span className="text-yellow-700 font-bold">거스름돈 (원화)</span>
            <span className="text-2xl font-bold text-yellow-700">{calc.changeKRW.toLocaleString()} 원</span>
          </div>
        </div>
      </ConfirmModal>

      <LoadingModal isOpen={isCreating} message={loadingMessage} />

      <PassportScanModal
        isOpen={showPassportScanModal}
        onClose={() => setShowPassportScanModal(false)}
        onScanComplete={(imageDataUrl) => {
          setScannedPassportImage(imageDataUrl)
          setPassportSkipped(false)
        }}
        onSkip={() => {
          setScannedPassportImage(null)
          setPassportSkipped(true)
        }}
      />

      {/* 환율 수정 (A: 매입 / B: 매각) */}
      <RateModificationModal
        isOpen={rateModalTarget === 'A'}
        currentRate={baseRateA}
        currencyCode={currencyA || ''}
        tradeType="buy"
        onConfirm={(newRate, reason) => {
          setRateOverrideA({ rate: newRate, reason })
          setRateModalTarget(null)
        }}
        onCancel={() => setRateModalTarget(null)}
      />
      <RateModificationModal
        isOpen={rateModalTarget === 'B'}
        currentRate={baseRateB}
        currencyCode={currencyB || ''}
        tradeType="sell"
        onConfirm={(newRate, reason) => {
          setRateOverrideB({ rate: newRate, reason })
          setRateModalTarget(null)
        }}
        onCancel={() => setRateModalTarget(null)}
      />

      {/* B 지급 외화 수정 */}
      <ForeignAmountModificationModal
        isOpen={showBAmountModal}
        calculatedAmount={calc.calcB}
        currencyCode={currencyB || ''}
        minUnit={currencyB ? CURRENCY_MIN_UNITS[currencyB] || 1 : 1}
        onConfirm={(newAmount) => {
          setBOverride(newAmount)
          setShowBAmountModal(false)
        }}
        onCancel={() => setShowBAmountModal(false)}
      />
    </>
  )
}

export default ExchangeTab
